import React, { useState, useEffect } from 'react';
import { DollarSign, Heart, X, ArrowLeftRight, Clock, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { apiService } from '../services/api';
import { useToast } from '../hooks/use-toast';
import TipResponseModal from './TipResponseModal';

const ReceivedTips = () => {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedTip, setSelectedTip] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchReceivedTips();
  }, []);

  const fetchReceivedTips = async () => {
    try {
      setLoading(true);
      const response = await apiService.tips.getReceivedTips();
      if (response.data.success) {
        setTips(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching received tips:', error);
      toast({
        title: "Error",
        description: "Failed to load tip offers.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTipResponse = (tip) => {
    if (tip.response) return; // Already responded
    
    setSelectedTip({
      swipe_id: tip.swipe_id,
      from_user_name: tip.from_user_name,
      amount: tip.amount,
      message: tip.message
    });
    setShowResponseModal(true);
  };

  const getStatusBadge = (tip) => {
    if (tip.is_matched) {
      return <Badge className="bg-green-500 text-white">Matched</Badge>;
    }
    
    if (tip.response === 'accepted') {
      return <Badge className="bg-green-500 text-white"><Check className="w-3 h-3 mr-1" />Accepted</Badge>;
    } else if (tip.response === 'rejected') {
      return <Badge className="bg-red-500 text-white"><X className="w-3 h-3 mr-1" />Declined</Badge>;
    } else if (tip.response === 'countered') {
      return <Badge className="bg-blue-500 text-white"><ArrowLeftRight className="w-3 h-3 mr-1" />Countered</Badge>;
    } else {
      return <Badge variant="outline" className="border-orange-300 text-orange-600"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <DollarSign className="w-6 h-6 mr-2 text-green-500" />
          Received Tip Offers
        </h1>
        <p className="text-gray-600 mt-1">
          Manage tip offers you've received from other users
        </p>
      </div>

      {tips.length === 0 ? (
        <Card className="p-8 text-center">
          <DollarSign className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Tip Offers Yet</h3>
          <p className="text-gray-500">
            When someone sends you a tip offer, you'll see it here and can choose to accept, decline, or counter.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {tips.map((tip) => (
            <Card key={tip.swipe_id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  {/* Profile Image */}
                  <div className="flex-shrink-0">
                    {tip.from_user_image ? (
                      <img
                        src={tip.from_user_image}
                        alt={tip.from_user_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                        <Heart className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Tip Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{tip.from_user_name}</h3>
                      {getStatusBadge(tip)}
                    </div>
                    
                    <div className="flex items-center space-x-2 mb-2">
                      <DollarSign className="w-4 h-4 text-green-500" />
                      <span className="text-xl font-bold text-green-600">${tip.amount}</span>
                      {tip.counter_amount && (
                        <>
                          <ArrowLeftRight className="w-4 h-4 text-blue-500" />
                          <span className="text-lg font-semibold text-blue-600">${tip.counter_amount}</span>
                        </>
                      )}
                    </div>

                    {tip.message && (
                      <div className="bg-gray-50 rounded-lg p-2 mb-2">
                        <p className="text-sm text-gray-700 italic">"{tip.message}"</p>
                      </div>
                    )}

                    <p className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(tip.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex-shrink-0">
                  {!tip.response && !tip.is_matched && (
                    <Button
                      onClick={() => handleTipResponse(tip)}
                      size="sm"
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      Respond
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Tip Response Modal */}
      {showResponseModal && selectedTip && (
        <TipResponseModal
          isOpen={showResponseModal}
          onClose={() => {
            setShowResponseModal(false);
            setSelectedTip(null);
          }}
          tipData={selectedTip}
          onResponse={() => {
            fetchReceivedTips(); // Refresh tips after response
          }}
        />
      )}
    </div>
  );
};

export default ReceivedTips;